import React, { useEffect, useRef, useState, useCallback } from 'react';
import { BrowserMultiFormatReader, NotFoundException } from '@zxing/library';
import { X, Camera, Loader2, ScanLine, AlertCircle } from 'lucide-react';

/**
 * BarcodeScanner Component
 * Uses ZXing library to scan barcodes from camera feed
 * 
 * Props:
 * - onScan: (barcode: string) => void - Called when barcode is detected
 * - onClose: () => void - Called when scanner is closed
 * - isOpen: boolean - Controls visibility
 */
const BarcodeScanner = ({ onScan, onClose, isOpen }) => {
  const videoRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastScanned, setLastScanned] = useState(null);
  const codeReaderRef = useRef(null);
  const streamRef = useRef(null);

  const stopScanner = useCallback(() => {
    if (codeReaderRef.current) {
      codeReaderRef.current.reset();
      codeReaderRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const startScanner = useCallback(async () => {
    if (!isOpen) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const codeReader = new BrowserMultiFormatReader();
      codeReaderRef.current = codeReader;
      
      // Get video devices
      const videoInputDevices = await codeReader.listVideoInputDevices();
      
      if (videoInputDevices.length === 0) {
        throw new Error('No camera devices found');
      }
      
      // Prefer back camera
      const backCamera = videoInputDevices.find(device => 
        device.label.toLowerCase().includes('back') || 
        device.label.toLowerCase().includes('rear') ||
        device.label.toLowerCase().includes('environment')
      ) || videoInputDevices[0];
      
      // Start scanning
      await codeReader.decodeFromVideoDevice(
        backCamera.deviceId,
        videoRef.current,
        (result, err) => {
          if (result) {
            const barcode = result.getText();
            if (barcode && barcode !== lastScanned) {
              setLastScanned(barcode);
              onScan(barcode);
            }
          }
          if (err && !(err instanceof NotFoundException)) {
            console.error('Scan error:', err);
          }
        }
      );
      
      // Get stream reference for cleanup
      if (videoRef.current && videoRef.current.srcObject) {
        streamRef.current = videoRef.current.srcObject;
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error('Scanner initialization error:', err);
      setError(err.message || 'Failed to start camera');
      setIsLoading(false);
    }
  }, [isOpen, lastScanned, onScan]);

  useEffect(() => {
    if (isOpen) {
      startScanner();
    } else {
      stopScanner();
    }
    
    return () => {
      stopScanner();
    };
  }, [isOpen, startScanner, stopScanner]);

  const handleClose = () => {
    stopScanner();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="barcode-scanner">
      {/* Header */}
      <div className="bg-black/80 backdrop-blur px-4 py-3 flex items-center justify-between">
        <button
          onClick={handleClose}
          className="w-10 h-10 bg-[#0A0A0A]/10 rounded-full flex items-center justify-center text-white"
          data-testid="barcode-scanner-close"
        >
          <X size={20} />
        </button>
        <div className="text-center">
          <p className="text-white text-sm font-medium">Scan Barcode</p>
          <p className="text-white/50 text-xs">Point camera at product barcode</p>
        </div>
        <div className="w-10" /> {/* Spacer */}
      </div>

      {/* Video Feed */}
      <div className="flex-1 relative overflow-hidden">
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          playsInline
          autoPlay
          muted
        />
        
        {/* Scanning Frame Overlay */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Darkened edges */}
          <div className="absolute inset-0 bg-black/50" />
          
          {/* Clear scanning area */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-40 bg-transparent">
            {/* Cut out the center */}
            <div className="relative w-full h-full">
              {/* Corner markers */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-[#000000] rounded-tl-lg" />
              <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-[#000000] rounded-tr-lg" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-[#000000] rounded-bl-lg" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-[#000000] rounded-br-lg" />
              
              {/* Scanning line animation */}
              <div className="absolute left-4 right-4 h-0.5 bg-black animate-scan-line" />
            </div>
          </div>
          
          {/* Clear window in the darkened overlay */}
          <div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-40"
            style={{ 
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
              borderRadius: '8px'
            }}
          />
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center">
            <Loader2 size={40} className="text-white animate-spin mb-4" />
            <p className="text-white/80 text-sm">Starting camera...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center px-6">
            <div className="w-16 h-16 bg-[#B85C3B]/20 rounded-full flex items-center justify-center mb-4">
              <AlertCircle size={32} className="text-[#B85C3B]" />
            </div>
            <p className="text-white text-center mb-2">{error}</p>
            <p className="text-white/50 text-sm text-center mb-4">
              Please ensure camera permissions are granted
            </p>
            <button
              onClick={startScanner}
              className="px-6 py-2 bg-black text-white rounded-lg text-sm"
            >
              Try Again
            </button>
          </div>
        )}
      </div>

      {/* Footer with instructions */}
      <div className="bg-black/80 backdrop-blur px-6 py-4 text-center">
        <div className="flex items-center justify-center gap-2 text-white/60 text-xs mb-2">
          <ScanLine size={14} />
          <span>Supports EAN, UPC, Code 128, QR codes</span>
        </div>
        {lastScanned && (
          <div className="bg-[#1B7340]/20 rounded-lg px-4 py-2 inline-block">
            <p className="text-[#1B7340] text-sm font-mono">{lastScanned}</p>
          </div>
        )}
      </div>

      {/* CSS for scan line animation */}
      <style>{`
        @keyframes scanLine {
          0%, 100% { top: 10%; }
          50% { top: 90%; }
        }
        .animate-scan-line {
          animation: scanLine 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default BarcodeScanner;
